import { authApi } from '@afinius/api-client/auth/api';
import type { LoginResponse } from '@afinius/api-client/auth/types';
import { setBearerToken, setTenantRefreshToken } from '@afinius/api-client/bearer-token';
import { afterResponseHooks } from '@afinius/api-client/client';
import { userApi } from '@afinius/api-client/user/api';
import { User } from '@afinius/api-client/user/types';
import { queryClient } from '@afinius/plugins/query';
import { refreshExpirationMinutes } from '@afinius/utilities/silent-sso';
import { defineStore } from 'pinia';
import { type Ref } from 'vue';
import { useRouter } from 'vue-router';

import { useLocalStorage } from '../useLocalStorage';
import { useUserStore } from './useUserStore';

interface Store {
  isAuthenticated: Ref<boolean>;
  impersonated: Ref<boolean>;
  ssoVerify: (token: string) => Promise<LoginResponse | undefined>;
  impersonate: (user: User) => Promise<void>;
  stopImpersonate: () => Promise<void>;
  resetSession: () => void;
  reset: () => void;
}

export const useAuthStore = defineStore('auth', (): Store => {
  const router = useRouter();
  const userStore = useUserStore();

  const [isAuthenticated, setIsAuthenticated] = useLocalStorage('is-authenticated', false);
  const [impersonated, setImpersonated] = useLocalStorage('impersonated', false);

  const loginSuccess = (response: LoginResponse) => {
    if (response.access_token) {
      setIsAuthenticated(true);
      setBearerToken(`Bearer ${response.access_token}`, response.expires_in);
    }

    if (response.refresh_token) {
      setTenantRefreshToken(response.refresh_token, response.refresh_expires_in ?? undefined);
    }

    return response;
  };

  const resetSession = () => {
    queryClient.clear();
    userStore.reset();

    setImpersonated(false);
  };

  const reset = () => {
    resetSession();

    setIsAuthenticated(false);
    setBearerToken(undefined);
    setTenantRefreshToken(undefined);
  };

  const ssoVerify = async (token: string) => {
    return authApi
      .ssoVerify({ token, refresh_expiration: refreshExpirationMinutes() })
      .then(loginSuccess)
      .catch((error) => {
        throw error;
      });
  };

  const impersonate = async (user: User) => {
    return userApi
      .impersonate(user.id)
      .then(loginSuccess)
      .then(() => {
        queryClient.clear();
        setTenantRefreshToken(undefined);
        userStore.fetchUser();
        setImpersonated(true);
      })
      .catch((error) => {
        throw error;
      });
  };

  const stopImpersonate = async (): Promise<void> => {
    await userApi.stopImpersonate().finally(() => {
      reset();
      window.location.reload();
    });
  };

  afterResponseHooks.push(async ({ response }) => {
    if (response.status === 503) {
      router.push({ name: 'maintenance' });
    }

    return response;
  });

  return {
    isAuthenticated,
    impersonated,
    ssoVerify,
    impersonate,
    stopImpersonate,
    resetSession,
    reset,
  };
});
